import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";
import MenuSetupService from "../../../services/KangarooServices/MenuSetupService";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import Select from "react-select";
import BreadCrumb from "../../../Components/Common/BreadCrumb";

const MenuCreate = () => {
  const { id } = useParams(); // id exists → update mode
  const navigate = useNavigate();

  // Data states
  const [selectedItem, setSelectedItem] = useState(null);
  const [menuTypes, setMenuTypes] = useState([]); // Menu types (header, footer, etc.)
  const [cmsPages, setCmsPages] = useState([]); // All standalone CMS pages
  const [nestedMenus, setNestedMenus] = useState([]); // Sub-menus under selected menu type
  const [subPageItems, setSubPageItems] = useState([]); // Blogs, events, etc. based on page type

  // UI states
  const [linkSource, setLinkSource] = useState("pages"); // "pages" or "listedSubPage"
  const [selectedPageType, setSelectedPageType] = useState(null);
  const [selectedSubPageItem, setSelectedSubPageItem] = useState(null);
  const [selectedCmsPage, setSelectedCmsPage] = useState(null); // For URL auto-fill only
  const [selectedMenuTypes, setSelectedMenuTypes] = useState([]);

  // Enum for sub-page types
  const pageTypeOptions = [
    { label: "Blogs", value: 1 },
    { label: "DestinationCountries", value: 2 },
    { label: "Events", value: 3 },
    { label: "TestPreparations", value: 4 },
  ];
  // Fetch menu detail for update
  const fetchMenuDetail = async () => {
    if (!id) return;
    try {
      const response = await MenuSetupService.getDetail(id);
      setSelectedItem(response);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load menu details");
    }
  };

  // Fetch menu types
  const fetchMenuTypes = async () => {
    try {
      const response = await MenuSetupService.getType({
        page: 1,
        pageSize: 100,
      });
      const types = (response?.items || []).map(({ id, name }) => ({
        id,
        name,
      }));
      setMenuTypes(types);
    } catch (error) {
      toast.error("Failed to fetch menu types");
    }
  };

  // Fetch all CMS pages (for linking)
  const fetchCmsPages = async () => {
    try {
      const response = await MenuSetupService.getParentPage();
      setCmsPages(response || []);
    } catch (error) {
      console.error(error);
    }
  };

  // Fetch nested menus under a menu type
  const fetchNestedMenus = async (menuTypeIds) => {
    if (!menuTypeIds || menuTypeIds.length === 0) {
      setNestedMenus([]);
      return;
    }

    try {
      const response = await MenuSetupService.getByTypeIds(menuTypeIds);

      // response is an ARRAY of menu types
      const allMenus =
        response?.flatMap((menuType) => menuType.menus || []) || [];

      // OPTIONAL: only allow top-level menus as parents
      const parentMenus = allMenus.filter((menu) => menu.parentId === null);

      // OPTIONAL: remove duplicates by menu id
      const uniqueMenus = Array.from(
        new Map(parentMenus.map((m) => [m.id, m])).values()
      );

      setNestedMenus(parentMenus);
    } catch (error) {
      console.error(error);
    }
  };

  // Fetch sub-page items (blogs, events, etc.)
  const fetchSubPageItems = async (pageTypeId) => {
    if (!pageTypeId) {
      setSubPageItems([]);
      return;
    }
    try {
      const response = await MenuSetupService.getListByPage(pageTypeId);
      setSubPageItems(response || []);
    } catch (error) {
      console.error(error);
    }
  };

  // Formik setup
  const formik = useFormik({
    initialValues: {
      id: selectedItem?.id || null,
      title: selectedItem?.title || "",
      url: selectedItem?.url || "",
      orderIndex: selectedItem?.orderIndex || 0,
      isActive: selectedItem?.isActive ?? true,
      menuTypeIds: selectedItem?.menuTypeIds || [], // ✅ array
      parentId: selectedItem?.parentId || null,
      isExternal: selectedItem?.isExternal ?? false,
      pageId: selectedItem?.pageId,
    },
    enableReinitialize: true,
    onSubmit: async (values) => {
      const payload = {
        ...values,
        isFromPage: linkSource === "pages",
      };

      try {
        values.id
          ? await MenuSetupService.update(payload)
          : await MenuSetupService.create(payload);

        toast.success(
          values.id ? "Menu updated successfully" : "Menu created successfully"
        );
        navigate("/menu");
      } catch (error) {
        toast.error("Something went wrong!");
      }
    },
  });

  // Options for selects
  const menuTypeOptions = menuTypes.map((type) => ({
    value: type.id,
    label: type.name,
  }));

  const nestedMenuOptions = nestedMenus.map((menu) => ({
    value: menu.id,
    label: menu.title,
  }));

  const cmsPageOptions = cmsPages.map((page) => ({
    value: page.id,
    label: page.title || page.name,
  }));

  const subPageItemOptions = subPageItems.map((item) => ({
    value: item.id,
    label: item.title || item.name || item.preparationName || "Untitled",
  }));

  // Selected values for controlled selects
  // const selectedMenuTypes = menuTypeOptions.filter((opt) =>
  //   formik.values.menuTypeIds.includes(opt.value)
  // );

  const selectedParentMenu = nestedMenuOptions.find(
    (opt) => opt.value === formik.values.parentId
  );

  // Handlers
  const handleMenuTypeChange = (options) => {
    setSelectedMenuTypes(options); // update select value
    const ids = options ? options.map((opt) => opt.value) : [];
    formik.setFieldValue("menuTypeIds", ids);
    formik.setFieldValue("parentId", null);
    fetchNestedMenus(ids);
  };

  // const handleParentMenuChange = (option) => {
  //   const parentId = option?.value || null;
  //   formik.setFieldValue("parentId", parentId);

  //   if (!parentId) {
  //     formik.setFieldValue("url", "");
  //     return;
  //   }

  //   const parent = nestedMenus.find((m) => m.id === parentId);
  //   if (!parent?.url) return;

  //   const parentUrl = parent.url.startsWith("/")
  //     ? parent.url
  //     : `/${parent.url}`;

  //   formik.setFieldValue("url", parentUrl);
  // };
  const handleParentMenuChange = (option) => {
    const parentId = option?.value || null;
    formik.setFieldValue("parentId", parentId);

    if (!parentId) {
      formik.setFieldValue("url", "");
      return;
    }

    const parent = nestedMenus.find((m) => m.id === parentId);
    if (!parent) return;

    // 👇 use url if exists, otherwise slug
    const basePath = parent.url || parent.slug;
    if (!basePath) return;

    const parentUrl = basePath.startsWith("/") ? basePath : `/${basePath}`;

    formik.setFieldValue("url", parentUrl);
  };

  const getParentUrlPrefix = () => {
    if (!formik.values.parentId) return "";

    const parent = nestedMenus.find((m) => m.id === formik.values.parentId);
    if (!parent) return "";

    // Use url if exists, otherwise slug
    const basePath = parent.url || parent.slug;
    if (!basePath) return "";

    return basePath.startsWith("/") ? basePath : `/${basePath}`;
  };

  const handleCmsPageChange = (option) => {
    setSelectedCmsPage(option);

    if (!option) {
      formik.setFieldValue("url", "");
      return;
    }

    const page = cmsPages.find((p) => p.id === option.value);
    if (!page?.slug) return;

    const parentPrefix = getParentUrlPrefix();
    formik.setFieldValue(
      "url",
      `${parentPrefix}/${page.slug}`.replace(/\/+/g, "/")
    );
    formik.setFieldValue("pageId", option?.value);
  };

  const handlePageTypeChange = (option) => {
    setSelectedPageType(option);
    setSelectedSubPageItem(null);
    // formik.setFieldValue("url", "");
    if (option) {
      fetchSubPageItems(option.value);
    } else {
      setSubPageItems([]);
    }
  };

  const handleSubPageItemChange = (option) => {
    setSelectedSubPageItem(option);

    if (!option || !selectedPageType) {
      formik.setFieldValue("url", "");
      return;
    }

    const item = subPageItems.find((i) => i.id === option.value);
    if (!item?.slug) return;

    const typeSlug = selectedPageType.label.toLowerCase().replace(/\s+/g, "-");

    const parentPrefix = getParentUrlPrefix();

    formik.setFieldValue(
      "url",
      `${parentPrefix}/${item.slug}`.replace(/\/+/g, "/")
    );
  };

  // Load initial data
  useEffect(() => {
    fetchMenuTypes();
    fetchCmsPages();
    fetchMenuDetail();
  }, []);

  // When menu type changes → load nested menus
  useEffect(() => {
    fetchNestedMenus(formik.values.menuTypeIds);
  }, [formik.values.menuTypeIds]);

  // When editing: pre-fill all UI states
  useEffect(() => {
    if (!selectedItem) return;

    // Set link source (pages or subpage)
    setLinkSource(selectedItem.isFromPage ? "pages" : "listedSubPage");

    // If linking to a CMS page → find and select it
    if (selectedItem.isFromPage && selectedItem.url && cmsPages.length > 0) {
      const slug = selectedItem.url.startsWith("/")
        ? selectedItem.url.slice(1)
        : selectedItem.url;
      const matched = cmsPages.find((p) => p.slug === slug);
      if (matched) {
        setSelectedCmsPage({
          value: matched.id,
          label: matched.title || matched.name,
        });
      }
    }

    // If linking to a sub-page → set type and load items
    if (!selectedItem.isFromPage && selectedItem.pageType) {
      const matchedType = pageTypeOptions.find(
        (t) => t.value === selectedItem.pageType
      );
      if (matchedType) {
        setSelectedPageType(matchedType);
        fetchSubPageItems(selectedItem.pageType);
      }
    }
  }, [selectedItem, cmsPages]);

  // After sub-page items load → auto-select the correct one
  useEffect(() => {
    if (
      linkSource !== "listedSubPage" ||
      !selectedItem ||
      !subPageItems.length ||
      !selectedItem.url
    )
      return;

    const slug = selectedItem.url.split("/").pop();
    const matched = subPageItems.find((i) => i.slug === slug);
    if (matched) {
      setSelectedSubPageItem({
        value: matched.id,
        label:
          matched.title ||
          matched.name ||
          matched.preparationName ||
          "Untitled",
      });
    }
  }, [subPageItems, selectedItem]);
  useEffect(() => {
    if (selectedItem?.menuTypes) {
      const initialSelected = selectedItem.menuTypes.map((type) => ({
        value: type.id,
        label: type.name,
      }));
      setSelectedMenuTypes(initialSelected);
    }
  }, [selectedItem]);
  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Menu Form"
          pageTitle="Menu"
          breadcrumbItems={[{ title: "Back to List", link: "/menu" }]}
        />

        <form onSubmit={formik.handleSubmit}>
          <Row>
            {/* Left Column */}
            <Col lg={6}>
              <Card>
                <CardBody>
                  <div className="mb-3">
                    <Label>
                      Title <span className="text-danger">*</span>
                    </Label>
                    <Input
                      name="title"
                      type="text"
                      value={formik.values.title}
                      onChange={formik.handleChange}
                    />
                  </div>

                  <div className="mb-3">
                    <Label>Order Index</Label>
                    <Input
                      name="orderIndex"
                      type="number"
                      value={formik.values.orderIndex}
                      onChange={formik.handleChange}
                    />
                  </div>

                  <div className="mb-3">
                    <Label>Menu Type</Label>
                    <Select
                      options={menuTypeOptions}
                      value={selectedMenuTypes} // ✅ this now shows initial selected
                      onChange={handleMenuTypeChange}
                      isMulti
                      isClearable
                      placeholder="Select Menu Type"
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({ ...base, padding: "0.3rem" }),
                      }}
                    />
                  </div>

                  <div className="mb-3">
                    <Label>Parent Menu (for nesting)</Label>
                    <Select
                      options={nestedMenuOptions}
                      value={selectedParentMenu}
                      onChange={handleParentMenuChange}
                      placeholder="Select Parent Menu"
                      isClearable
                      isDisabled={formik.values.menuTypeIds.length === 0}
                      menuPortalTarget={document.body}
                      styles={{
                        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                        control: (base) => ({ ...base, padding: "0.3rem" }),
                      }}
                    />
                  </div>

                  <div className="mb-3">
                    <Label className="form-label">Is External Link?</Label>
                    <div>
                      <input
                        type="checkbox"
                        id="isExternal"
                        checked={formik.values.isExternal}
                        onChange={() =>
                          formik.setFieldValue(
                            "isExternal",
                            !formik.values.isExternal
                          )
                        }
                        className="form-check-input me-2"
                      />
                      <label htmlFor="isExternal" className="form-check-label">
                        Yes, this is an external URL
                      </label>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>

            {/* Right Column */}
            <Col lg={6}>
              <Card>
                <CardBody>
                  <div className="mb-3">
                    <Label>
                      URL <span className="text-danger">*</span>
                    </Label>
                    {/* <Input
                      name="url"
                      type="text"
                      value={formik.values.url}
                      onChange={formik.handleChange}
                      placeholder="/example-page"
                    /> */}
                    <Input
                      name="url"
                      value={formik.values.url}
                      onChange={formik.handleChange}
                      // readOnly={!!formik.values.parentId}
                      placeholder="/example-page"
                    />
                  </div>

                  <div className="mb-4">
                    <Label>Link Source</Label>
                    <div>
                      <FormGroup check inline>
                        <Input
                          type="radio"
                          id="pages" // <-- add this
                          name="linkSource"
                          value="pages"
                          checked={linkSource === "pages"}
                          onChange={() => setLinkSource("pages")}
                        />
                        <Label check htmlFor="pages">
                          Link to a Page
                        </Label>
                      </FormGroup>
                      <FormGroup check inline className="ms-4">
                        <Input
                          type="radio"
                          id="listedSubPage" // <-- add this
                          name="linkSource"
                          value="listedSubPage"
                          checked={linkSource === "listedSubPage"}
                          onChange={() => setLinkSource("listedSubPage")}
                        />
                        <Label check htmlFor="listedSubPage">
                          Link to a Sub-page (Blog, Event, etc.)
                        </Label>
                      </FormGroup>
                    </div>
                  </div>

                  {/* Link to CMS Page */}
                  {linkSource === "pages" && (
                    <div className="mb-3">
                      <Label>Select Page</Label>
                      <Select
                        options={cmsPageOptions}
                        value={selectedCmsPage}
                        onChange={handleCmsPageChange}
                        placeholder="Choose a page to auto-fill URL"
                        isClearable
                        menuPortalTarget={document.body}
                        styles={{
                          menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                          control: (base) => ({ ...base, padding: "0.3rem" }),
                        }}
                      />
                    </div>
                  )}

                  {/* Link to Sub-page (Blog, Event, etc.) */}
                  {linkSource === "listedSubPage" && (
                    <>
                      <div className="mb-3">
                        <Label>Page Type</Label>
                        <Select
                          options={pageTypeOptions}
                          value={selectedPageType}
                          onChange={handlePageTypeChange}
                          placeholder="Select type"
                          isClearable
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            control: (base) => ({ ...base, padding: "0.3rem" }),
                          }}
                        />
                      </div>

                      <div className="mb-3">
                        <Label>Select Item</Label>
                        <Select
                          options={subPageItemOptions}
                          value={selectedSubPageItem}
                          onChange={handleSubPageItemChange}
                          placeholder="Select item to auto-fill URL"
                          isClearable
                          isDisabled={!selectedPageType}
                          menuPortalTarget={document.body}
                          styles={{
                            menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                            control: (base) => ({ ...base, padding: "0.3rem" }),
                          }}
                        />
                      </div>
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>

          <div className="text-end mt-3">
            <button type="submit" className="btn btn-primary">
              {formik.values.id ? "Update Menu" : "Create Menu"}
            </button>
          </div>
        </form>
      </Container>
    </div>
  );
};

export default MenuCreate;
